/* eslint-disable */
import React, { useMemo, useContext, useEffect, useState } from 'react';

import { IconButton, Tooltip } from '@material-ui/core';
import styled from '@emotion/styled';
import {
    Clear as ClearIcon,
    Lock as LockIcon,
    Event as EventIcon,
    LocalShippingOutlined as LocalShippingOutlinedIcon,
} from '@material-ui/icons';

import { RouteCardGrayText, ConfirmationBadge, PlanningListItem, JiraTitleField } from '../blocks';
import { css } from '@emotion/react';
import { MODALS, ROUTE_COLORS } from '../constants';
import { isResourceAcceptingBookings, routePartner } from '../planningDerivations';
import routeStatusOptions from '../../../constants/routeStatusOptions';
import { useCustomerStops, useRouteTimeLimits, useEstimatedRouteTime, useRouteExceedsWindowCeiling } from '../hooks';
import { useTotalWeight, useTotalCubes, useItemQuantity, useRouteRevenue } from '@/hooks';
import { PlanningUiContext } from '../context';
import { poundFormatter, integerFormatter } from '@/constants/formats';
import { useClientUser } from '@/hooks/useClientUser';
import { useBusinessTimezone } from '@/hooks/useBusinessTimezone';
import { delWindowToFormattedDateRange } from '@/utilities/delWindowToFormattedDate';
import { ROUTE_STATUS_SUBSCRIPTION } from '../graphql/queries';
import { useMutation } from '@apollo/client';
import { UPDATE_ROUTE } from '../graphql/mutations';
import RouteColorPicker from './RouteColorPicker';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import { colors } from '@/styles';
import FTLStopHelpers from '@/utilities/FTLStopHelpers';
import { genAttributes } from '@onward-delivery/core';
import { ADD_ORDER_TO_COMPLETED_ROUTE, PLAN_PAGE_SHOW_RATES } from '@/constants/featureFlags';
import { useDriverPay } from '../hooks/useDriverPay';
import { useResourcesById } from '../hooks/useResourcesById';
import { computeRoutePreviewStats, sumOrderRevenue } from '../util/routePreviewStats';
import { getConfirmationLabel, getRouteConfirmationStatus, isUnresolvedConfirmation } from '../util/orderConfirmation';
import { routeDisplayName } from '@/utilities/routeNames';
import { currencyFormatter } from '@/components/Claims/formatters';
import { UPSERT_ROUTE } from '../graphql/mutations';
import { ROUTE_READONLY_FIELDS } from '@onward-delivery/core';
import { omit } from 'lodash';

// Max width the route-name field may grow to before it ellipsizes, in px.
const NAME_FIELD_MAX_WIDTH = 240;

// ── Redesigned card layout ─────────────────────────────────────────────
// Left color strip + padded body. The strip sits flush against the card's
// left edge, so PlanningListItem's own padding is zeroed for this card
// (see cardResetCSS on the instance below) and the body owns the padding.

const CardRow = styled.div`
    /* Grid/footer hairlines (#ededed) and the financial band (#f6f7f9) read
       correctly on the default white card, but on hover/selected the card
       background turns #ececec: the hairlines nearly disappear and the band
       (lighter than #ececec) looks inverted/lightened. Drive both from CSS vars
       and darken them in those states so they stay subtly recessed over #ececec. */
    --grid-border: #ededed;
    --fin-band-bg: #f6f7f9;
    display: flex;
    align-items: stretch;
    width: 100%;
    min-width: 0;
    &:hover {
        --grid-border: #d5d5d5;
        --fin-band-bg: #dcdfe3;
    }
    &[data-selected='true'] {
        --grid-border: #d5d5d5;
        --fin-band-bg: #dcdfe3;
    }
`;

const cardResetCSS = css`
    padding: 0 !important;
`;

const ColorStrip = styled.button`
    width: 5px;
    flex: none;
    align-self: stretch;
    border: none;
    padding: 0;
    margin: 0;
    cursor: pointer;
`;

const CardBody = styled.div`
    flex: 1;
    min-width: 0;
    padding: 12px 14px;
`;

const HeaderRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`;

// Groups the truck icon and the (content-width) title so space-between only
// pushes the action icons to the far right — the icon stays flush to the title
// instead of being spread away from it.
const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
`;

// Truck icon left of the route name. Tinted with the route color and, like the
// edge strip, opens the color picker on click.
const TruckColorButton = styled.button`
    flex: none;
    display: inline-flex;
    align-items: center;
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    cursor: pointer;
    line-height: 0;
`;

const truckIconCSS = css`
    font-size: 20px;
`;

const ActionIcons = styled.div`
    display: flex;
    align-items: center;
    gap: 2px;
    flex: none;
    color: ${colors.greys.primary};
`;

const actionButtonCSS = css`
    padding: 2px !important;
`;

const actionIconCSS = css`
    font-size: 16px;
    color: ${colors.greys.primary};
`;

const warningIconCSS = css`
    font-size: 16px;
    color: ${colors.reds.primary};
`;

// Quiet enough not to compete with the route name.
const ClosedResourceTag = styled.div`
    display: inline-flex;
    align-items: center;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.02em;
    color: ${colors.greys.primary};
    background: rgba(0, 0, 0, 0.05);
    border-radius: 3px;
    padding: 1px 6px;
    margin: 0 0 10px;
`;

const SendToLabel = styled.div`
    font-size: 11.5px;
    font-weight: 600;
    color: ${colors.greys.primary};
    margin: 1px 0 10px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const FinancialBand = styled.div`
    display: flex;
    background: var(--fin-band-bg);
    border-radius: 7px;
    overflow: hidden;
    margin-bottom: 11px;
`;

const FinCell = styled.div`
    flex: 1;
    min-width: 0;
    padding: 6px 10px;
`;

const FinLabel = styled.div`
    font-size: 9.5px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${colors.greys.subtitle};
`;

const FinValue = styled.div`
    font-size: 14px;
    font-weight: 700;
    color: ${(props) => props.color || colors.greys[7]};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const MetricGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    border: 1px solid var(--grid-border);
    border-radius: 7px;
    overflow: hidden;
    margin-bottom: 2px;
`;

const MetricCell = styled.div`
    padding: 6px 10px;
    min-width: 0;
    ${(props) => props.borderRight && 'border-right: 1px solid var(--grid-border);'}
    ${(props) => props.borderBottom && 'border-bottom: 1px solid var(--grid-border);'}
`;

const MetricLabel = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 9.5px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${colors.greys.subtitle};
`;

const MetricValue = styled.div`
    font-size: 13.5px;
    font-weight: 700;
    color: ${(props) => props.color || colors.greys[7]};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const FooterRow = styled.div`
    border-top: 1px solid var(--grid-border);
    margin-top: 10px;
    padding-top: 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    font-size: 12px;
    font-weight: 500;
    color: ${colors.greys.secondary};
`;

const FooterResource = styled.span`
    color: ${colors.greys.secondary};
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const FooterTimeframe = styled.span`
    color: ${colors.greys.secondary};
    flex: none;
    max-width: 60%;
    text-align: right;
    white-space: pre-wrap;
`;

const StatusChipRow = styled.div`
    margin-top: 9px;
`;

const chipCSS = css`
    display: inline-block;
    font-size: 11.5px;
    font-weight: 600;
    padding: 2px 10px;
`;

const footerGrayCSS = css`
    font-size: inherit;
`;

// Line-icon path data lifted verbatim from the design reference. Rendered at
// 13px with a muted stroke to match the metric grid spec.
const METRIC_ICON_PATHS = {
    cube: (
        <>
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
            <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
            <line x1="12" y1="22.08" x2="12" y2="12" />
        </>
    ),
    layers: (
        <>
            <polygon points="12 2 2 7 12 12 22 7 12 2" />
            <polyline points="2 17 12 22 22 17" />
            <polyline points="2 12 12 17 22 12" />
        </>
    ),
    weight: (
        <>
            <path d="M12 3a4 4 0 0 0-3.6 5.7C6 9.7 4 13 4 16.5A4.5 4.5 0 0 0 8.5 21h7a4.5 4.5 0 0 0 4.5-4.5c0-3.5-2-6.8-4.4-7.8A4 4 0 0 0 12 3z" />
            <path d="M9 7.5h6" />
        </>
    ),
    pin: (
        <>
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
        </>
    ),
    route: (
        <>
            <circle cx="6" cy="19" r="2.3" />
            <circle cx="18" cy="5" r="2.3" />
            <path d="M8.5 19H14a3 3 0 0 0 0-6h-4a3 3 0 0 1 0-6h5.3" />
        </>
    ),
    clock: (
        <>
            <circle cx="12" cy="12" r="9" />
            <polyline points="12 7 12 12 15 14" />
        </>
    ),
};

const MetricIcon = ({ name }) => (
    <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke={colors.greys.subtitle}
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ flex: 'none' }}
    >
        {METRIC_ICON_PATHS[name]}
    </svg>
);

function RouteListItem({ route, routeIndex, subscribeToMore, isSelected, previewOrders, setSelectedRoute }) {
    const ordersByKey = useMemo(() => {
        return Object.fromEntries(
            (route?.orders || []).map((mapping) => [
                mapping.order_id,
                { ...mapping.order, routeMappingType: mapping.type },
            ])
        );
    }, [route]);

    const {
        circles,
        partner_carriers,
        partner_contractors,
        preferences_route_time_limit,
        preferences_route_eod_limit,
        preferences_route_eod_limit_next_day,
        preferences_bypass_optimize,
    } = useClientUser();
    const tz = useBusinessTimezone();

    const { setModalOpen, setNotification, setActionableRoute, setError } = useContext(PlanningUiContext);

    // Keyed on route_id (the subscription's only variable) so cache broadcasts
    // that refresh the route object don't tear down and re-open the websocket.
    useEffect(() => {
        let unsubscribe;

        unsubscribe = subscribeToMore({
            document: ROUTE_STATUS_SUBSCRIPTION,
            variables: { route_id: route.route_id },
        });

        // Unsubscribe
        if (unsubscribe) return () => unsubscribe();
    }, [route.route_id, subscribeToMore]);

    const confirmationStatus = useMemo(() => getRouteConfirmationStatus(route, ordersByKey), [ordersByKey, route]);

    const routeName = routeDisplayName(route);

    const orderValues = useMemo(() => Object.values(ordersByKey), [ordersByKey]);

    const totalRevenue = useRouteRevenue(route);
    const estimatedRouteTime = useEstimatedRouteTime(route);

    // Calculate driver pay using the new hook
    const showRates = circles?.[PLAN_PAGE_SHOW_RATES];
    const { driverPay, loading: driverPayLoading } = useDriverPay(route, showRates);

    const customerStops = useCustomerStops(route);

    const [colorAnchorEl, setColorAnchorEl] = useState(null);
    const [updateRouteColor] = useMutation(UPDATE_ROUTE);

    const handleColorSelect = (color) => {
        updateRouteColor({
            variables: {
                route_id: route.route_id,
                update: { route_color: color },
            },
            optimisticResponse: {
                update_routes_by_pk: {
                    ...route,
                    route_color: color,
                },
            },
        });
        setColorAnchorEl(null);
    };

    const [upsertRoute, { loading: routeUpsertLoading }] = useMutation(UPSERT_ROUTE, {
        update: (cache, { data: { insert_routes_one } }) => {
            if (insert_routes_one) {
                cache.modify({
                    fields: {
                        routes(existingRoutes = []) {
                            const newRouteRef = cache.identify(insert_routes_one);
                            if (newRouteRef) {
                                // Check if route already exists in cache
                                const exists = existingRoutes.some((ref) => ref.__ref === newRouteRef);
                                if (!exists) {
                                    return [...existingRoutes, { __ref: newRouteRef }];
                                }
                            }
                            return existingRoutes;
                        },
                    },
                });
            }
        },
        onError: (error) => {
            setError(error, 'Error updating route');
        },
        onCompleted: () => {
            setNotification({
                severity: 'success',
                message: 'Route successfully updated',
            });
        },
    });

    const updateRoute = ({ route, orders, drivers }) => {
        const remaining = omit(route, ROUTE_READONLY_FIELDS);
        return upsertRoute({
            variables: {
                route: remaining,
                route_update_cols: Object.keys(remaining),
                drivers: drivers || [],
                orders: orders || [],
            },
        });
    };

    const updateRouteAlias = (route, routeAlias) => {
        const route_alias = routeAlias.trim();
        // The resource-name check stops a typed-back derived name from freezing a copy into
        // route_alias.
        if (
            route?.route_alias === route_alias ||
            routeAlias === `Route ${route.route_number}` ||
            route_alias === route?.resource?.name
        )
            return;

        updateRoute({
            route: {
                ...route,
                route_alias: route_alias,
            },
        });
    };

    const setShowRemoveRouteModal = () => {
        if (
            route.status !== routeStatusOptions.PLANNING.value &&
            !(route.stopsByRouteId || []).some((s) => s.stop_completion_time)
        ) {
            setNotification({
                severity: 'warning',
                message: 'Cannot remove route that is in progress or complete.',
            });
        } else {
            setActionableRoute(route);
            setModalOpen(MODALS.ROUTE_REMOVE);
        }
    };

    const setShowRescheduleModal = () => {
        if (
            route.status !== routeStatusOptions.PLANNING.value &&
            !(route.stopsByRouteId || []).some((s) => s.stop_completion_time)
        ) {
            setNotification({
                severity: 'warning',
                message: 'Cannot reschedule route that is in progress or complete.',
            });
        } else {
            setActionableRoute(route);
            setModalOpen(MODALS.ROUTE_RESCHEDULE);
        }
    };

    const setShowUnlockModal = () => {
        if (route.status === 'complete' && !circles?.[ADD_ORDER_TO_COMPLETED_ROUTE]) {
            setNotification({
                severity: 'warning',
                message: 'This route has already been completed.',
            });
        } else {
            setActionableRoute(route);
            setModalOpen(MODALS.ROUTE_UNLOCK);
        }
    };

    const routeExceedsTimeLimits = useRouteTimeLimits(
        route,
        preferences_route_time_limit,
        preferences_route_eod_limit,
        preferences_route_eod_limit_next_day
    );

    const routeExceedsWindowCeiling = useRouteExceedsWindowCeiling(route, ordersByKey);

    const firstOrder = orderValues[0] || {};
    // getStopSequence cloneDeeps every stop — memoize so it only reruns when
    // the route's stops actually change.
    const sortedStops = useMemo(() => FTLStopHelpers.getStopSequence(route.stopsByRouteId), [route.stopsByRouteId]);

    const routeTimeframe = useMemo(() => {
        const { zip } = genAttributes(firstOrder);
        const firstStop = sortedStops[0];
        const lastStop = sortedStops[sortedStops.length - 1];
        return delWindowToFormattedDateRange(
            firstStop?.stop_start_time || firstStop?.del_window_start,
            lastStop?.end ? lastStop?.stop_start_time || lastStop?.del_window_start : lastStop?.del_window_end,
            firstOrder?.[zip]
        );
    }, [firstOrder, route, sortedStops]);

    const includeShipperRates = !!circles?.[PLAN_PAGE_SHOW_RATES];

    const existingOrders = useMemo(
        () => (route.orders || []).map((mapping) => ordersByKey[mapping.order_id]).filter(Boolean),
        [route, ordersByKey]
    );

    const revenueTotals = useMemo(
        () => sumOrderRevenue(existingOrders, includeShipperRates),
        [existingOrders, includeShipperRates]
    );

    // Live preview: when this route is selected and orders are checked on the map,
    // project what the route's stats (revenue, load, stop count) would become if
    // those orders were assigned. When present, the rendered stats switch to these
    // pending values (shown in the preview color) until the user actually assigns,
    // at which point the route's real totals take over.
    const ordersToAdd = previewOrders;
    const isPreview = ordersToAdd.length > 0;

    const previewStats = useMemo(() => {
        if (!isPreview) return null;
        return computeRoutePreviewStats({ route, existingOrders, ordersToAdd, includeShipperRates });
    }, [isPreview, route, existingOrders, ordersToAdd, includeShipperRates]);

    // Values actually rendered: the pending preview when orders are selected onto
    // this route, otherwise the route's real, committed totals.
    const displayRevenue = isPreview ? previewStats : revenueTotals;
    const displayCustomerStops = isPreview ? previewStats.customerStops : customerStops;
    const previewColor = colors.blues.primary;

    // Load metrics (cubes / pieces / weight) reflect the pending orders in preview
    // mode, mirroring how useTotalCubes/useTotalWeight iterate the route's orders.
    const displayOrderValues = useMemo(
        () => (isPreview ? [...existingOrders, ...ordersToAdd] : orderValues),
        [isPreview, existingOrders, ordersToAdd, orderValues]
    );
    const displayCubes = useTotalCubes(displayOrderValues);
    const displayWeight = useTotalWeight(displayOrderValues);
    const displayPieces = useItemQuantity(displayOrderValues);

    const shipperRevenue = displayRevenue.shipperRevenue;
    const carrierRevenue = displayRevenue.carrierRevenue;
    const hasFinancials = shipperRevenue > 0 || carrierRevenue > 0;
    const splitText =
        shipperRevenue > 0 && carrierRevenue > 0 ? `${((carrierRevenue / shipperRevenue) * 100).toFixed(2)}%` : null;

    const renderConfirmationStatus = () => {
        const chip = (status, text) => (
            <StatusChipRow>
                <ConfirmationBadge status={status} css={chipCSS}>
                    {text}
                </ConfirmationBadge>
            </StatusChipRow>
        );

        if (confirmationStatus === 'rejected') {
            return chip('rejected', '1 or more confirmations rejected');
        }

        if (route.need_to_optimize && (!preferences_bypass_optimize || route.sending_to === 'marketplace')) {
            return chip('pending', 'Route modified, map and resubmit');
        }

        if (route.need_to_submit && route.sending_to) {
            return chip('pending', 'Route modified, please resubmit');
        }

        // Ranked below the resubmit prompts — a resubmit resets these to pending
        // anyway — but above plain pending, which would otherwise hide the fact
        // that we already reached the line and got nothing.
        if (isUnresolvedConfirmation(confirmationStatus)) {
            return chip(
                confirmationStatus,
                `1 or more customers not reached (${getConfirmationLabel(confirmationStatus)})`
            );
        }

        if (confirmationStatus === 'pending' && route.sending_to) {
            return chip('pending', 'Pending Customer Confirmation');
        }

        if (confirmationStatus === 'confirmed') {
            return route.status !== 'planning'
                ? chip('confirmed', 'All customer timeframes confirmed')
                : chip('pending', 'Timeframes confirmed, please resubmit');
        }

        return chip('pending', 'Submit route to confirm timeframes');
    };

    const partneredCarrier = useMemo(
        () => routePartner(route, { partner_carriers, partner_contractors }),
        [route, partner_carriers, partner_contractors]
    );

    const sendToLabel = useMemo(() => {
        switch (route.sending_to) {
            case 'partner':
                return partneredCarrier?.business_name || '--';
            case 'marketplace':
                return 'Onward';
            case 'internal':
                return 'Internal';
            default:
                return 'Pending';
        }
    }, [route.sending_to, partneredCarrier]);

    const resource = useResourcesById()[route?.resource_id];

    // Muted, not hidden — the route still runs.
    const resourceClosed = useMemo(() => {
        return !!resource && !isResourceAcceptingBookings(resource, route?.scheduled_delivery, tz);
    }, [resource, route?.scheduled_delivery, tz]);

    const stripColor = route?.route_color || ROUTE_COLORS[routeIndex];

    return (
        <PlanningListItem
            hover={true}
            css={cardResetCSS}
            onClick={(e) => {
                if (e.target.closest('[data-testid^="route-color-swatch-"]')) return;
                if (colorAnchorEl) return;
                setSelectedRoute((prev) => (prev === route.route_id ? null : route.route_id));
            }}
            selected={isSelected}
        >
            <CardRow data-selected={isSelected || undefined}>
                <ColorStrip
                    data-testid={`route-color-swatch-${route.route_id}`}
                    aria-label="Change route color"
                    style={{ background: stripColor }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                        e.stopPropagation();
                        setColorAnchorEl(e.currentTarget);
                    }}
                />
                <CardBody>
                    <HeaderRow
                        css={
                            resourceClosed
                                ? css`
                                      opacity: 0.65;
                                  `
                                : undefined
                        }
                    >
                        <HeaderLeft>
                            <TruckColorButton
                                data-testid={`route-color-swatch-truck-${route.route_id}`}
                                aria-label="Change route color"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setColorAnchorEl(e.currentTarget);
                                }}
                            >
                                <LocalShippingOutlinedIcon css={truckIconCSS} style={{ color: stripColor }} />
                            </TruckColorButton>
                            <JiraTitleField
                                css={css`
                                    min-width: 0;
                                    max-width: 100%;
                                    /* Sizer-mirror: stack the input and an invisible text twin in the
                                       same grid cell. The twin is real text in the real font, so the
                                       browser sizes the column to the glyphs exactly — the field (and
                                       its clickable/editable area) hugs the name with no measuring. */
                                    .MuiInputBase-root {
                                        display: inline-grid;
                                        max-width: min(100%, ${NAME_FIELD_MAX_WIDTH}px);
                                    }
                                    .MuiInputBase-root::after {
                                        content: attr(data-value);
                                        grid-area: 1 / 1;
                                        white-space: pre;
                                        visibility: hidden;
                                        padding-right: 6px;
                                        font-size: 15px;
                                        font-weight: 700;
                                        letter-spacing: inherit;
                                    }
                                    .MuiInputBase-root input {
                                        grid-area: 1 / 1;
                                        width: 100%;
                                        min-width: 24px;
                                        font-size: 15px;
                                        font-weight: 700;
                                        color: ${colors.greys[7]};
                                        padding: 2px 0;
                                        text-overflow: ellipsis;
                                    }
                                `}
                                placeholder={routeName}
                                defaultValue={routeName}
                                // Mirror text for the ::after sizer. size={1} keeps the input's own
                                // intrinsic width from setting the column floor.
                                InputProps={{ 'data-value': routeName }}
                                inputProps={{ size: 1 }}
                                onInput={(e) => {
                                    e.target.parentElement.dataset.value = e.target.value;
                                }}
                                onBlur={(e) => updateRouteAlias(route, e.target.value)}
                                onClick={(e) => {
                                    e.stopPropagation();
                                }}
                            />
                        </HeaderLeft>
                        <ActionIcons>
                            {(routeExceedsTimeLimits || routeExceedsWindowCeiling) && (
                                <Tooltip
                                    title={
                                        routeExceedsTimeLimits && routeExceedsWindowCeiling
                                            ? 'This route exceeds your time limits and has stops whose delivery window ends past your ceiling.'
                                            : routeExceedsWindowCeiling
                                            ? 'This route has one or more stops whose delivery window ends past your ceiling.'
                                            : 'This route exceeds your configured time limits.'
                                    }
                                    placement="bottom"
                                    arrow
                                >
                                    <ReportProblemOutlinedIcon css={warningIconCSS} />
                                </Tooltip>
                            )}
                            {route.status !== routeStatusOptions.PLANNING.value && (
                                <Tooltip
                                    title={
                                        route.status === 'complete' ? "Can't unlock completed routes" : 'Unlock to edit'
                                    }
                                    placement="bottom"
                                    arrow
                                >
                                    <IconButton
                                        css={actionButtonCSS}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowUnlockModal();
                                        }}
                                    >
                                        <LockIcon css={actionIconCSS} />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {circles?.['plan-reschedule-v1'] && route.status === routeStatusOptions.PLANNING.value && (
                                <Tooltip title="Reschedule" placement="bottom" arrow>
                                    <IconButton
                                        css={actionButtonCSS}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowRescheduleModal();
                                        }}
                                    >
                                        <EventIcon css={actionIconCSS} />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Tooltip title="Remove route" placement="bottom" arrow>
                                <IconButton
                                    css={actionButtonCSS}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowRemoveRouteModal();
                                    }}
                                >
                                    <ClearIcon css={actionIconCSS} />
                                </IconButton>
                            </Tooltip>
                        </ActionIcons>
                    </HeaderRow>

                    <SendToLabel>{sendToLabel}</SendToLabel>
                    {resourceClosed ? <ClosedResourceTag>Not accepting new orders</ClosedResourceTag> : null}

                    {hasFinancials && (
                        <FinancialBand>
                            {shipperRevenue > 0 && (
                                <FinCell>
                                    <FinLabel>Shipper</FinLabel>
                                    <FinValue color={isPreview ? previewColor : colors.greys[7]}>
                                        {currencyFormatter(shipperRevenue)}
                                    </FinValue>
                                </FinCell>
                            )}
                            {carrierRevenue > 0 && (
                                <FinCell>
                                    <FinLabel>Carrier</FinLabel>
                                    <FinValue color={isPreview ? previewColor : colors.greens.primary}>
                                        {currencyFormatter(carrierRevenue)}
                                    </FinValue>
                                </FinCell>
                            )}
                            {splitText && (
                                <FinCell>
                                    <FinLabel>Split</FinLabel>
                                    <FinValue color={isPreview ? previewColor : colors.greys.secondary}>
                                        {splitText}
                                    </FinValue>
                                </FinCell>
                            )}
                        </FinancialBand>
                    )}

                    <MetricGrid>
                        <MetricCell borderRight borderBottom>
                            <MetricLabel>
                                <MetricIcon name="cube" />
                                Cu Ft
                            </MetricLabel>
                            <MetricValue color={isPreview ? previewColor : undefined}>
                                {integerFormatter.format(displayCubes || 0)}
                            </MetricValue>
                        </MetricCell>
                        <MetricCell borderRight borderBottom>
                            <MetricLabel>
                                <MetricIcon name="layers" />
                                Pcs
                            </MetricLabel>
                            <MetricValue color={isPreview ? previewColor : undefined}>
                                {integerFormatter.format(displayPieces || 0)}
                            </MetricValue>
                        </MetricCell>
                        <MetricCell borderBottom>
                            <MetricLabel>
                                <MetricIcon name="weight" />
                                Wt
                            </MetricLabel>
                            <MetricValue color={isPreview ? previewColor : undefined}>
                                {poundFormatter.format(displayWeight || 0)}
                            </MetricValue>
                        </MetricCell>
                        <MetricCell borderRight>
                            <MetricLabel>
                                <MetricIcon name="pin" />
                                Stops
                            </MetricLabel>
                            <MetricValue color={isPreview ? previewColor : undefined}>
                                {displayCustomerStops}
                            </MetricValue>
                        </MetricCell>
                        <MetricCell borderRight>
                            <MetricLabel>
                                <MetricIcon name="route" />
                                Dist
                            </MetricLabel>
                            <MetricValue>{route?.estimated_driving_distance || 'N/A'}</MetricValue>
                        </MetricCell>
                        <MetricCell>
                            <MetricLabel>
                                <MetricIcon name="clock" />
                                Time
                            </MetricLabel>
                            <MetricValue>{estimatedRouteTime || 'N/A'}</MetricValue>
                        </MetricCell>
                    </MetricGrid>

                    <FooterRow>
                        <FooterResource>
                            Resource:{' '}
                            {resource?.name ? (
                                resource.name
                            ) : (
                                <RouteCardGrayText css={footerGrayCSS}>Unassigned</RouteCardGrayText>
                            )}
                        </FooterResource>
                        <FooterTimeframe>
                            {routeTimeframe ? (
                                routeTimeframe
                            ) : (
                                <RouteCardGrayText css={footerGrayCSS}>No timeframe</RouteCardGrayText>
                            )}
                        </FooterTimeframe>
                    </FooterRow>

                    {renderConfirmationStatus()}
                </CardBody>
            </CardRow>
            <RouteColorPicker
                open={Boolean(colorAnchorEl)}
                anchorEl={colorAnchorEl}
                currentColor={route?.route_color || ROUTE_COLORS[routeIndex]}
                onClose={() => setColorAnchorEl(null)}
                onSelect={handleColorSelect}
            />
        </PlanningListItem>
    );
}

export default React.memo(RouteListItem);
